import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem
} from '@mui/material';
import DashboardLayout from '../components/DashboardLayout';
import dayjs from 'dayjs';
import { getOfferBySlug, deleteOffer } from '../services/offerService';
import { useAuth } from '../context/AuthContext';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArticleIcon from '@mui/icons-material/Article';
import ScheduleIcon from '@mui/icons-material/Schedule';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { FAQAccordion } from '../components';

const OfferDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { user, isAuthenticated, loading: authLoading } = useAuth();

  const fetchOffer = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getOfferBySlug(slug);
      setOffer(data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch offer details. The offer might not exist or has been removed.');
      console.error('Error fetching offer:', err);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    // Wait for auth to load before fetching offer
    if (!authLoading) {
      fetchOffer();
    }
  }, [fetchOffer, authLoading]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    navigate(`/offers/edit/${offer._id}`);
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this offer? This action cannot be undone.')) {
      try {
        setDeleteLoading(true);
        await deleteOffer(offer._id);
        navigate('/offers');
      } catch (err) {
        setError('Failed to delete offer. Please try again.');
        console.error('Error deleting offer:', err);
      } finally {
        setDeleteLoading(false);
        handleMenuClose();
      }
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Loading...">
        <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Container>
      </DashboardLayout>
    );
  }

  if (error || !offer) {
    return (
      <DashboardLayout title="Offer Not Found">
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || 'Offer not found'}
          </Alert>
          <Button
            component={RouterLink}
            to="/offers"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
          >
            Back to Offers
          </Button>
        </Container>
      </DashboardLayout>
    );
  }

  const isExpired = new Date() > new Date(offer.expirationDate);
  
  // More robust author check - try different possible user ID fields
  const isAuthor = isAuthenticated && user && offer.author && (
    user.id === offer.author._id || 
    user._id === offer.author._id || 
    user.id === offer.author.id ||
    user._id === offer.author.id
  );

  const offerActions = isAuthor ? (
    <IconButton onClick={handleMenuOpen}>
      <MoreVertIcon />
    </IconButton>
  ) : null;

  return (
    <DashboardLayout title={offer?.title || "Offer Details"} actions={offerActions} maxWidth="md">
      <Box sx={{ maxWidth: 'md', mx: 'auto', px: 3, py: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Button
            component={RouterLink}
            to="/offers"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            sx={{ mb: 2 }}
          >
            Back to Offers
          </Button>
        </Box>

      <Card sx={{ mb: 3 }}>
        {offer.featuredImage && (
          <CardMedia
            component="img"
            height="400"
            image={offer.featuredImage}
            alt={offer.title}
            sx={{ objectFit: 'cover' }}
          />
        )}
        
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocalOfferIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
                <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold' }}>
                  {offer.title}
                </Typography>
                {isExpired && (
                  <Chip 
                    label="Expired" 
                    color="error" 
                    sx={{ ml: 2 }}
                  />
                )}
              </Box>
            </Box>
            

          </Box>

          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ mb: 4, lineHeight: 1.6 }}
          >
            {offer.description}
          </Typography>

          {/* FAQAccordion Demo Section */}
          <Box sx={{ mb: 4 }}>
            <FAQAccordion
              title="MoonBrew FAQs"
              items={[
                { question: 'What are the benefits of MoonBrew?*', answer: 'MoonBrew offers a variety of benefits including ...' },
                { question: 'How might MoonBrew affect my sleep?', answer: 'MoonBrew is designed to support restful sleep by ...' },
                { question: 'Is there melatonin in MoonBrew?', answer: 'No, MoonBrew does not contain melatonin.' },
                { question: 'Is there caffeine in MoonBrew?', answer: 'No, MoonBrew is caffeine-free.' },
                { question: 'Is there sugar in MoonBrew?', answer: 'MoonBrew contains no added sugar.' },
                { question: 'How is MoonBrew made?', answer: 'MoonBrew is crafted using ...' },
                { question: 'Can I drink MoonBrew while pregnant?', answer: 'Please consult your healthcare provider before consuming MoonBrew while pregnant.' },
              ]}
            />
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <ScheduleIcon sx={{ mr: 1 }} />
              Offer Details
            </Typography>
            
            <Box sx={{ display: 'grid', gap: 2, mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 'medium', minWidth: 120 }}>
                  Expires:
                </Typography>
                <Typography 
                  variant="body1" 
                  color={isExpired ? 'error.main' : 'text.primary'}
                  sx={{ fontWeight: isExpired ? 'medium' : 'normal' }}
                >
                  {dayjs(offer.expirationDate).format('MMM D, YYYY h:mm A')}
                  {isExpired && ' (Expired)'}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 'medium', minWidth: 120 }}>
                  Created:
                </Typography>
                <Typography variant="body1">
                  {dayjs(offer.createdAt).format('MMM D, YYYY')}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 'medium', minWidth: 120 }}>
                  Author:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ width: 24, height: 24, mr: 1 }}>
                    {offer.author?.name?.charAt(0) || 'A'}
                  </Avatar>
                  <Typography variant="body1">
                    {offer.author?.name || 'Anonymous'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              component="a"
              href={offer.purchaseUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              size="large"
              startIcon={<ShoppingCartIcon />}
              disabled={isExpired}
              sx={{ 
                minWidth: 200,
                py: 1.5,
                fontSize: '1.1rem'
              }}
            >
              {isExpired ? 'Offer Expired' : 'Get This Offer'}
            </Button>
            
            {offer.blogLink && (
              <Button
                component="a"
                href={offer.blogLink}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                size="large"
                startIcon={<ArticleIcon />}
                sx={{ py: 1.5 }}
              >
                Read Related Article
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Menu for author actions */}
      {isAuthor && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleEdit}>
            <EditIcon sx={{ mr: 1 }} />
            Edit Offer
          </MenuItem>
          <MenuItem onClick={handleDelete} disabled={deleteLoading}>
            <DeleteIcon sx={{ mr: 1 }} />
            Delete Offer
          </MenuItem>
        </Menu>
      )}
    </Box>
    </DashboardLayout>
  );
};

export default OfferDetail; 